import { useMemo, useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

// constants
import { COMPONENT_QUERY } from '../../../constants/api';
import {
  ACTUAL_VS_BUDGET_BRANCHES_MONTHLY,
  ACTUAL_VS_BUDGET_BRANCHES_WEEKLY,
  ACTUAL_VS_BUDGET_CHANNEL,
  ACTUAL_VS_BUDGET_MONTHLY,
  ACTUAL_VS_BUDGET_WEEKLY,
  BUDGET_VS_ACTUAL_MONTHLY_TOTAL,
  BUDGET_VS_ACTUAL_WEEKLY_TOTAL,
  VIEW_TYPE_MONTHLY,
  VIEW_TYPE_WEEKLY,
} from '../constants';

// services
import api from '../../../services/api';

// selectors
import selectors from '../../../modules/selectors/datePickerSelectors';
import {
  actualVsBudgetMonthlyHeaders,
  actualVsBudgetMonthlyRowData,
  actualVsBudgetWeeklyHeaders,
  actualVsBudgetWeeklyRowData,
  seprateCombinedChannel,
} from '../services/ActualVsBudgetServices';

export const useActualVsBudgetHook = () => {
  const { userStores, userData } = useSelector((state) => state.auth);
  const { transformedDataWithDate } = useSelector(selectors?.datePickerCombinedData);
  const currentYear = userData?.data?.current_year || transformedDataWithDate[0]?.currYear;
  const options = useMemo(() => {
    const stores = userStores.map((store) => ({
      label: store.branch_name,
      value: store.branch_number,
      ...store,
    }));
    const GaleMarkets = stores.filter((store) => store.value === 1);

    if (GaleMarkets?.length) {
      return [
        GaleMarkets[0],
        ...stores
          .filter((store) => store.value !== 1)
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    }
    return [
      ...stores.filter((store) => store.value !== 1).sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [userStores]);
  const [storeId, setStoreId] = useState(options?.[0]?.value);
  const [viewType, setViewType] = useState(VIEW_TYPE_WEEKLY);
  const [resetStore, setResetStore] = useState(false);
  const [tableData, setTableData] = useState({ rowData: [], columnData: [] });
  const [actualVsBudgetFilters, setActualVsBudgetFilters] = useState([]);
  const [actualVsBudgetFiltersBranches, setActualVsBudgetFiltersBranches] = useState([]);
  const [actualVsBudgetFiltersChannels, setActualVsBudgetFiltersChannels] = useState({});
  const [channels, setChannels] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState(channels);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState({
    branchTableLoading: false,
    channelTableLoading: false,
  });
  const [error, setError] = useState({ branchTableError: null, channelTableError: null });
  const [branchTableData, setBranchTableData] = useState({
    rowData: [],
    columnData: [],
    filteredData: [],
    branchDataFetched: false,
    branchDataRetry: false,
    viewChange: false,
  });
  const [selectedBranches, setSelectedBranches] = useState([
    ...new Set(options?.map((item) => item?.value)),
  ]);
  const [isTimeoutInProcess, setIsTimeoutInProcess] = useState(false);
  const [isActualVsBudgetBranchesReportLoading, setIsActualVsBudgetBranchesReportLoading] =
    useState(false);
  const [isActualVsBudgetChannelReportLoading, setIsActualVsBudgetChannelReportLoading] =
    useState(false);
  const initialLoading = useRef(true);
  const channelLoading = useRef(true);
  const debounceTimeoutRef = useRef(null);
  const handleStoreIdChange = (store) => {
    setStoreId(store);
  };

  const handleResetForStore = () => {
    setResetStore(true);
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setBranchTableData((prevState) => ({ ...prevState, viewChange: true }));
      setViewType(newView);
    }
  };

  const getPayload = (
    key,
    actualVsBudgetFilters,
    totalKey,
    normal = true,
    downloadReportPayload = false
  ) => {
    const channelFilterObj = {
      col: 'channel',
      op: 'IN',
      val: seprateCombinedChannel(selectedChannels, userData?.data?.channel_roll_up_map),
    };
    const modifiedPayload = {
      [key]: {
        filters: downloadReportPayload
          ? [...actualVsBudgetFilters, channelFilterObj]
          : actualVsBudgetFilters,
      },
      [totalKey]: {
        filters: [...actualVsBudgetFilters, channelFilterObj],
      },
    };

    const normalPayload = {
      [key]: {
        filters: actualVsBudgetFilters,
      },
    };

    return normal ? normalPayload : modifiedPayload;
  };

  /**
   *
   * @param payload
   * @param key
   * @param branch
   */
  async function fetchActualVsBudget(payload, key, branch = false) {
    try {
      const response = await api.post(`${COMPONENT_QUERY}?component=${key}`, payload);

      return response?.data;
    } catch (error) {
      setError((prevState) => ({
        ...prevState,
        [branch ? 'branchTableError' : 'channelTableError']: true,
      }));
      console.error('error in API actual vs budget data--->', error);
    }
  }

  const fetchData = async () => {
    try {
      setActualVsBudgetFiltersChannels({});
      setFilteredData([]);
      setTableData({ rowData: [], columnData: [] });
      setError((prevState) => ({ ...prevState, channelTableError: null }));
      setIsLoading((prevState) => ({ ...prevState, channelTableLoading: true }));

      // the component key to be set according to the toogle button for monthly and weekly
      const key =
        viewType === VIEW_TYPE_MONTHLY ? ACTUAL_VS_BUDGET_MONTHLY : ACTUAL_VS_BUDGET_WEEKLY;
      const totalKey =
        viewType === VIEW_TYPE_MONTHLY
          ? BUDGET_VS_ACTUAL_MONTHLY_TOTAL
          : BUDGET_VS_ACTUAL_WEEKLY_TOTAL;
      const payload = getPayload(key, actualVsBudgetFilters, totalKey, false, true);

      setActualVsBudgetFiltersChannels(payload);
      const result = await fetchActualVsBudget(payload, key);

      if (viewType === VIEW_TYPE_MONTHLY) {
        const rowDataMonthly = actualVsBudgetMonthlyRowData({
          data: result?.data?.budget_vs_actual_monthly?.result,
        });
        const rowTotalDataMonthly = actualVsBudgetMonthlyRowData({
          data: result?.data?.budget_vs_actual_monthly_total?.result,
          total: true,
        });

        if (selectedChannels?.length) {
          const filteredRowData = rowDataMonthly?.filter((item) =>
            selectedChannels?.includes(item?.channel)
          );

          result?.data?.budget_vs_actual_monthly_total?.result?.length > 0 &&
            setFilteredData([rowTotalDataMonthly?.[0], ...filteredRowData]);
        }
        result?.data?.budget_vs_actual_monthly_total?.result?.length > 0 &&
          setTableData({
            rowData: [rowTotalDataMonthly?.[0], ...rowDataMonthly],
            columnData: actualVsBudgetMonthlyHeaders(
              result?.data?.budget_vs_actual_monthly?.result?.[0]?.ytd_month
            ),
          });
      } else {
        const rowDataWeekly = actualVsBudgetWeeklyRowData({
          data: result?.data?.budget_vs_actual_weekly?.result,
        });
        const rowTotalDataWeekly = actualVsBudgetWeeklyRowData({
          data: result?.data?.budget_vs_actual_weekly_total?.result,
          total: true,
        });

        if (selectedChannels?.length) {
          const filteredRowData = rowDataWeekly?.filter((item) =>
            selectedChannels?.includes(item?.channel)
          );

          result?.data?.budget_vs_actual_weekly_total?.result?.length > 0 &&
            setFilteredData([rowTotalDataWeekly?.[0], ...filteredRowData]);
        }
        result?.data?.budget_vs_actual_weekly_total?.result?.length > 0 &&
          setTableData({
            rowData: [rowTotalDataWeekly?.[0], ...rowDataWeekly],
            columnData: actualVsBudgetWeeklyHeaders(result?.data?.budget_vs_actual_weekly?.result),
          });
      }
    } catch (error) {
      console.error('error in fetching actual vs budget data--->', error);
    } finally {
      setIsLoading((prevState) => ({ ...prevState, channelTableLoading: false }));
      initialLoading.current = false;
    }
  };

  const fetchChannelData = async () => {
    try {
      const payload = getPayload(ACTUAL_VS_BUDGET_CHANNEL, [
        {
          col: 'fiscal_year',
          op: '==',
          val: currentYear,
        },
      ]);
      const channelDataForFilter = await fetchActualVsBudget(payload, ACTUAL_VS_BUDGET_CHANNEL);
      const options = channelDataForFilter?.data?.budget_vs_actual_channel_filter?.result?.map(
        (eachChannel) => ({
          label: eachChannel?.channel,
          value: eachChannel?.channel,
        })
      );

      setSelectedChannels(options?.map((item) => item?.value)); // initially all channels are selected
      setChannels(options); // this is used to set the channels in the dropdown
    } catch (error) {
      console.error('error in fetching actual vs budget channels--->', error);
      setError((prevState) => ({ ...prevState, channelTableError: true }));
    }
  };

  const fetchBranchData = async () => {
    try {
      setActualVsBudgetFiltersBranches([]);
      setBranchTableData((prevState) => ({ ...prevState, rowData: [], columnData: [] }));
      setError((prevState) => ({ ...prevState, branchTableError: null }));
      setIsLoading((prevState) => ({ ...prevState, branchTableLoading: true }));

      // the component key to be set according to the toogle button for monthly and weekly
      const key =
        viewType === VIEW_TYPE_MONTHLY
          ? ACTUAL_VS_BUDGET_BRANCHES_MONTHLY
          : ACTUAL_VS_BUDGET_BRANCHES_WEEKLY;
      const channelForBranches = channels?.map((item) => item?.value);
      const actualVsBudgetFiltersForBranches = [
        {
          col: 'store_id',
          op: 'IN',
          val: [...new Set(options?.map((item) => item?.value))],
        },
        { col: 'fiscal_year', op: 'in', val: currentYear },
        {
          col: 'channel',
          op: 'IN',
          val: seprateCombinedChannel(channelForBranches, userData?.data?.channel_roll_up_map),
        },
      ];

      setActualVsBudgetFiltersBranches(actualVsBudgetFiltersForBranches);
      const payload = getPayload(key, actualVsBudgetFiltersForBranches);
      const result = await fetchActualVsBudget(payload, key, true);

      let branchRowData, branchColumnData, filteredBranchRowData;

      if (viewType === VIEW_TYPE_MONTHLY) {
        branchRowData = actualVsBudgetMonthlyRowData({
          data: result?.data?.budget_vs_actual_branches_monthly?.result,
          branchData: true,
          options,
        });
        branchColumnData = actualVsBudgetMonthlyHeaders(
          result?.data?.budget_vs_actual_branches_monthly?.result?.[0]?.ytd_month,
          true
        );
      } else if (viewType === VIEW_TYPE_WEEKLY) {
        branchRowData = actualVsBudgetWeeklyRowData({
          data: result?.data?.budget_vs_actual_branches_weekly?.result,
          branchData: true,
          options,
        });
        branchColumnData = actualVsBudgetWeeklyHeaders(
          result?.data?.budget_vs_actual_branches_weekly?.result,
          true
        );
      }
      filteredBranchRowData = [...(branchRowData || [])]?.filter((item) =>
        selectedBranches?.includes(item?.store_id)
      );
      setBranchTableData((prevState) => ({
        ...prevState,
        rowData: branchRowData,
        columnData: branchColumnData,
        filteredData: filteredBranchRowData,
        branchDataFetched: true,
        viewChange: false,
      }));
    } catch (error) {
      console.error('error in fetching actual vs budget branchData table--->', error);
      setBranchTableData((prevState) => ({
        ...prevState,
        branchDataRetry: true,
        viewChange: false,
      }));
    } finally {
      setIsLoading((prevState) => ({ ...prevState, branchTableLoading: false }));
    }
  };

  // when channel changes then table data filter should be taken care here
  const filterSelectedChannels = (value) => {
    value?.length !== 0 && setIsTimeoutInProcess(true);
    const updatedChannels = seprateCombinedChannel(value, userData?.data?.channel_roll_up_map);

    setSelectedChannels(updatedChannels);
  };

  // when branch changes then branchTable data should be taken care here added to optimise the performance
  const filterSelectedBranches = (value) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      setSelectedBranches(value);
      const filteredRowData = [...(branchTableData?.rowData || [])]?.filter((item) =>
        value?.includes(item?.store_id)
      );

      setBranchTableData((prevState) => ({ ...prevState, filteredData: filteredRowData }));
    }, 1000);
  };

  useEffect(() => {
    if (storeId && currentYear) {
      setActualVsBudgetFilters([
        { col: 'store_id', op: 'IN', val: storeId },
        { col: 'fiscal_year', op: 'in', val: [currentYear] },
      ]);
    }
  }, [storeId, currentYear]);

  useEffect(() => {
    if (
      !channels?.length &&
      currentYear &&
      !isLoading?.channelTableLoading &&
      channelLoading.current
    ) {
      fetchChannelData();
      channelLoading.current = false;
    }

    if (
      !branchTableData.branchDataFetched &&
      currentYear &&
      !isLoading?.branchTableLoading &&
      channels?.length
    ) {
      fetchBranchData();
    } else if (
      (branchTableData.branchDataRetry || branchTableData.viewChange) &&
      !isLoading?.branchTableLoading &&
      channels?.length
    ) {
      // added this to retry once if the branch data is not fetched and when the view is changed
      fetchBranchData();
      setBranchTableData((prevState) => ({ ...prevState, branchDataRetry: false }));
    }

    if (
      actualVsBudgetFilters?.length > 0 &&
      selectedChannels?.length &&
      !isLoading?.channelTableLoading
    ) {
      fetchData();
    }
  }, [
    currentYear,
    actualVsBudgetFilters,
    viewType,
    channels,
    branchTableData.branchDataFetched,
    branchTableData.branchDataRetry,
  ]);

  // this useEffect is added to avoid the multiple api calls when the selected channels are changed rapidly
  useEffect(() => {
    if (actualVsBudgetFilters?.length > 0 && selectedChannels?.length) {
      if (selectedChannels?.length && !isLoading?.channelTableLoading && !initialLoading.current) {
        setIsTimeoutInProcess(true);
        const timer = setTimeout(() => {
          fetchData();
          setIsTimeoutInProcess(false);
        }, 1200); // 1200ms delay

        // Cleanup function to clear the timeout if the value changes before the delay is over
        return () => {
          clearTimeout(timer);
          setIsTimeoutInProcess(false);
        };
      }
    }
  }, [selectedChannels]);

  return {
    userStores,
    options,
    isLoading,
    error,
    initialLoading,
    storeId,
    tableData,
    handleStoreIdChange,
    handleResetForStore,
    resetStore,
    viewType,
    handleViewChange,
    filterSelectedChannels,
    filteredData,
    selectedChannels,
    channels,
    filterSelectedBranches,
    selectedBranches,
    branchTableData,
    isTimeoutInProcess,
    setIsActualVsBudgetBranchesReportLoading,
    isActualVsBudgetBranchesReportLoading,
    actualVsBudgetFiltersBranches,
    actualVsBudgetFiltersChannels,
    isActualVsBudgetChannelReportLoading,
    setIsActualVsBudgetChannelReportLoading,
  };
};
